import { useEffect, useRef, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { repOptions } from "@/lib/leads";
import { activityDefaultSetter, linkedRep } from "@/lib/linkedRep";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, todayIST } from "@/lib/format";
import { ACTIVITY_COUNTS, BLANK_ACTIVITY, entryToForm, toActivityBody, validateActivity } from "@/lib/activityForm";

const DateSetterFields = ({ date, setterId, setters, setterErr, onPick }) => (
  <>
    <label className="block"><span className="field-label">Date</span>
      <input type="date" data-testid="activity-date-input" value={date} max={todayIST()} onChange={(e) => onPick(e.target.value, setterId)} className="inp" />
    </label>
    <label className="block"><span className="field-label">Setter</span>
      <select data-testid="activity-setter-select" value={setterId} onChange={(e) => onPick(date, e.target.value)} className={`inp ${setterErr ? "inp-invalid" : ""}`}>
        <option value="">Pick setter</option>
        {setters.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      {setterErr && <span data-testid="activity-setter-error" className="field-error mt-1 block">{setterErr}</span>}
    </label>
  </>
);

const CountFields = ({ f, errors, showErrors, onCount }) => ACTIVITY_COUNTS.map(([k, label]) => (
  <label key={k} className="block"><span className="field-label">{label}</span>
    <input type="number" inputMode="numeric" data-testid={`activity-${k}-input`} value={f[k]} placeholder="0"
      onChange={(e) => onCount(k, e.target.value)}
      className={`inp font-mono ${showErrors && errors[k] ? "inp-invalid" : ""}`} />
    {showErrors && errors[k] && <span data-testid={`activity-${k}-error`} className="field-error mt-1 block">{errors[k]}</span>}
  </label>
));

const ActivityActions = ({ existing, date }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <p data-testid="activity-form-mode" className="text-xs text-slate-400">
      {existing ? `Updating the existing entry for ${fmtDate(date)}` : `New entry for ${fmtDate(date)}`}
    </p>
    <button type="submit" data-testid="activity-save-btn" className="btn-primary"><Save className="h-4 w-4" /> Save activity</button>
  </div>
);

const useDefaultSetter = (reps, entries, date, setSetterId, setF) => {
  const { user } = useAuth();
  const touched = useRef(false);
  const defaultId = activityDefaultSetter(linkedRep(user, reps));
  useEffect(() => {
    if (touched.current || !defaultId) return;
    setSetterId(defaultId);
    setF(entryToForm(entries.find((a) => a.setter_id === defaultId && a.date === date)));
  }, [defaultId, entries, date, setSetterId, setF]);
  return touched;
};

export const ActivityForm = ({ reps, entries, onSaved }) => {
  const setters = repOptions(reps, "setter");
  const [date, setDate] = useState(todayIST());
  const [setterId, setSetterId] = useState("");
  const [f, setF] = useState(BLANK_ACTIVITY);
  const [setterErr, setSetterErr] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const touched = useDefaultSetter(reps, entries, date, setSetterId, setF);
  const errors = validateActivity(f);
  const existing = entries.find((a) => a.setter_id === setterId && a.date === date);

  const pick = (d, s) => {
    touched.current = true;
    setDate(d);
    if (s !== setterId) setSetterErr("");
    setSetterId(s);
    setF(entryToForm(entries.find((a) => a.setter_id === s && a.date === d)));
  };
  const onCount = (k, v) => { setF((p) => ({ ...p, [k]: v })); setShowErrors(true); };

  const save = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!setterId) setSetterErr("Pick a setter");
    if (!setterId || Object.keys(errors).length) return;
    try {
      const { data } = await api.put("/activity", toActivityBody(date, setterId, f));
      onSaved(data);
      toast.success(existing ? "Activity updated" : "Activity saved");
      setShowErrors(false);
    } catch (err) {
      toast.error(errMsg(err, "Save failed"));
    }
  };

  return (
    <form onSubmit={save} data-testid="activity-form" className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <DateSetterFields date={date} setterId={setterId} setters={setters} setterErr={setterErr} onPick={pick} />
        <CountFields f={f} errors={errors} showErrors={showErrors} onCount={onCount} />
      </div>
      <ActivityActions existing={existing} date={date} />
    </form>
  );
};
